import {JsonPipe, NgClass, NgStyle} from '@angular/common';
import {Component, DestroyRef, HostListener, OnInit, ViewChild} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {MetaData, NgEventBus} from 'ng-event-bus';
import {of} from 'rxjs';
import {switchMap} from 'rxjs/operators';
import {AdditionalsComponent} from '../../additionals/additionals.component';
import {AnalysisComponent} from '../../analysis/analysis.component';
import {CertificationComponent} from '../../certification/certification.component';
import {NLR_TABS as TABS} from '../../common/constants';
import {PlanLodgementService} from '../../common/service/plan-lodgement.service';
import {PlanModelService} from '../../common/service/plan-model.service';
import {DesignComponent} from '../../design/design.component';
import {MapService} from '../../design/services/map.service';
import {InterestsComponent} from '../../interests/interests.component';
import {LodgePlanComponent} from '../../lodge-plan/lodge-plan.component';
import {NlrPlanDetailService} from '../../plan-detail/plan-detail.service';
import {PlanInformationComponent} from '../../plan-information/plan-information.component';
import {DigitalPlanComponent} from "../../digital-plan/digital-plan.component";

@Component({
    selector: 'nlr-right',
    imports: [
        PlanInformationComponent,
        DesignComponent,
        LodgePlanComponent,
        CertificationComponent,
        InterestsComponent,
        AnalysisComponent,
        AdditionalsComponent,
        NgClass,
        NgStyle,
        JsonPipe,
        DigitalPlanComponent
    ],
    templateUrl: './right.component.html',
    styleUrl: './right.component.scss'
})
export class RightComponent implements OnInit {
    @ViewChild(PlanInformationComponent)
    planInformationComponent: PlanInformationComponent;

    @ViewChild(AnalysisComponent)
    analysisComponent: AnalysisComponent;

    @ViewChild(CertificationComponent)
    certificationComponent: CertificationComponent;

    @ViewChild(InterestsComponent)
    interestsComponent: InterestsComponent;

    @ViewChild(LodgePlanComponent)
    lodgePlanComponent: LodgePlanComponent;

    @ViewChild(AdditionalsComponent)
    additionalsComponent: AdditionalsComponent;

    @ViewChild(DesignComponent)
    designComponent: DesignComponent;

    @ViewChild(DigitalPlanComponent)
    digitalPlanComponent: DigitalPlanComponent;

    selectedTab: TABS;
    showDebugModels = false;

    protected readonly TABS = TABS;

    private readonly title = 'RightComponent';

    constructor(
        private readonly destroyRef: DestroyRef,
        private readonly eventBus: NgEventBus,
        private readonly nlrPlanDetailService: NlrPlanDetailService,
        private readonly planModelService: PlanModelService,
        private readonly planLodgementService: PlanLodgementService,
        private readonly mapService: MapService
    ) {}

    ngOnInit(): void {
        this.eventBus
            .on('nlr:tabChange:*')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((meta: MetaData) => {
                this.selectedTab = meta.data;
                this.nlrPlanDetailService.selectedTab = TABS[this.selectedTab].toLowerCase();
                if (this.selectedTab === TABS.DESIGN) {
                    console.log(this.title + ' : showDesign');
                    if (this.planModelService.readonly) {
                        this.eventBus.cast('initiateDesignContent', {
                            reload: false,
                            splitter: true,
                            initProjection: false
                        });
                    } else {
                        // Save plan before going to Design tab. if not saved new added features won't be available in the attribute viewer
                        const subscription = this.planLodgementService
                            .save({showAlertOnSave: false, showWarningForCertifiedPlan: false})
                            .pipe(
                                switchMap(() => {
                                    if (this.designComponent.initialized) {
                                        return this.mapService.moveToExtent(this.planModelService.getModel().formerTenures);
                                    } else {
                                        return of({});
                                    }
                                })
                            )
                            .subscribe(() => {
                                if (!this.designComponent.initialized) {
                                    this.eventBus.cast('initiateDesignContent', {
                                        reload: false,
                                        splitter: true,
                                        initProjection: false
                                    });
                                } else {
                                    this.eventBus.cast('nlr:collapsePanel', 'left');
                                }
                            });
                        this.eventBus.cast('nlr:busy', subscription);
                    }
                }
                setTimeout(() => {
                    document.getElementById('right-split-area').scrollTo({top: 0, left: 0, behavior: 'smooth'});
                }, 200);
            });
    }

    @HostListener('document:keydown.control.q', ['$event']) onKeydownHandler(event: KeyboardEvent) {
        event.preventDefault();
        console.log('CTRL + Q');
        this.showDebugModels = !this.showDebugModels;
    }

    debugModelDisplayTypes = ['Raw', 'Sanitised', 'JSOG', 'Pristine', 'Pristine JSOG'];

    selectedDebugModelDisplayTypes = ['Raw'];

    onClickDebugModelDisplayType(debugModelDisplayType: string) {
        const idx = this.selectedDebugModelDisplayTypes.indexOf(debugModelDisplayType);
        if (idx === -1) {
            this.selectedDebugModelDisplayTypes.push(debugModelDisplayType);
        } else {
            this.selectedDebugModelDisplayTypes.splice(idx, 1);
        }
    }

    getDebugModel(debugModelDisplayType: string) {
        switch (debugModelDisplayType) {
            case 'Raw':
                return this.planModelService.getModel();
            case 'Sanitised':
                return this.planModelService.getSanitisedModel();
            case 'JSOG':
                return this.planModelService.getModelForSerialisation();
            case 'Pristine':
                return this.planModelService.getPristineModel();
            case 'Pristine JSOG':
                return this.planModelService.getPristineModelForSerialisation();
        }
    }

    getDebugModelPreWidth() {
        return Math.floor((100 / this.selectedDebugModelDisplayTypes.length) * Math.pow(10, 8)) / Math.pow(10, 8) + '%';
    }
}
